// Reports → Costs and prices (decision 163): a vendor's prices (Mata's first) against USDA and the other vendors, our
// cost, our tier prices and the margins, with the KPIs at the top and a product's weeks in a chart. Owner and office.
import type { CostDash, CostDashRow } from '@morales/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { TrendChart, compact } from '../components/charts';
import { PeriodBar, presets, usePeriod } from '../components/PeriodBar';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { Trend } from '../components/Signals';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatQty } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const RANGES = presets('d30', 'd7', 'd90', 'd365');
type SortKey = 'name' | 'packs' | 'sales' | 'gm' | 'cost' | 'vendor' | 'change' | 'other' | 'usda' | 'vs';

export function CostsPricesPage() {
  const { t, intlLocale } = useLocale();
  const period = usePeriod(RANGES);
  const [vendorRaw, setVendor] = useSearchParam('vendor');
  const [itemRaw, setItem] = useSearchParam('item');
  const [q, setQ] = useState('');
  const query = useQuery({
    queryKey: qk.costDash(period.from, period.to, vendorRaw),
    queryFn: ({ signal }) => api.costDash({ from: period.from, to: period.to, ...(vendorRaw ? { vendor: vendorRaw } : {}) }, signal),
    placeholderData: keepPreviousData,
  });
  const sorters = useMemo(
    () => ({
      name: byText<CostDashRow>((r) => r.itemName),
      packs: byNumber<CostDashRow>((r) => r.packs),
      sales: byNumber<CostDashRow>((r) => r.sales),
      gm: byNumber<CostDashRow>((r) => r.marginPct),
      cost: byNumber<CostDashRow>((r) => r.cost),
      vendor: byNumber<CostDashRow>((r) => r.vendorNow),
      change: byNumber<CostDashRow>((r) => r.vendorChangePct),
      other: byNumber<CostDashRow>((r) => r.bestOther?.price ?? null),
      usda: byNumber<CostDashRow>((r) => r.usda?.mid ?? null),
      vs: byNumber<CostDashRow>((r) => r.vsUsdaPct),
    }),
    [],
  );
  const { sorted, head } = useSort<CostDashRow, SortKey>(sorters, { key: 'sales', dir: -1 }, ['packs', 'sales', 'gm', 'cost', 'vendor', 'change', 'other', 'usda', 'vs']);
  return (
    <div className="desk-page" data-testid="costs-prices">
      <header className="desk-head">
        <div>
          <h1>{t('costsPricesTitle')}</h1>
          <p className="muted">{t('costsPricesHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <PeriodBar presets={RANGES} period={period} />
        {query.data ? (
          <label className="tool">
            <span className="label">{t('cpVendor')}</span>
            <select value={query.data.vendor?.id ?? ''} onChange={(e) => { setVendor(e.target.value); setItem(''); }}>
              {query.data.vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <QueryView query={query}>
        {(d) => (
          <div className="stack" style={{ opacity: query.isPlaceholderData ? 0.6 : 1 }}>
            <Kpis d={d} />
            <div className="chart-grid">
              <section className={itemRaw ? undefined : 'wide'} data-testid="cp-weeks">
                <h2>{t('cpWeeksTitle')}</h2>
                {d.weeks.length === 0 ? (
                  <Empty>{t('noSalesInPeriod')}</Empty>
                ) : (
                  <TrendChart
                    label={t('cpWeeksTitle')}
                    points={d.weeks.map((w) => ({ key: w.week, axis: formatDate(w.week, intlLocale, 'monthDay'), title: t('weekOf').replace('{date}', formatDate(w.week, intlLocale, 'date')) }))}
                    bar={{ name: t('revenue'), values: d.weeks.map((w) => w.sales), color: 'var(--viz-1)' }}
                    line={{ name: t('grossProfit'), values: d.weeks.map((w) => w.margin), color: 'var(--viz-2)' }}
                    format={(v) => formatMoney(v, intlLocale)}
                    axisFormat={(v) => compact(v, intlLocale)}
                    height={220}
                  />
                )}
              </section>
              {itemRaw ? <ItemWeeks itemId={itemRaw} d={d} onClose={() => setItem('')} /> : null}
            </div>
            <div className="toolbar">
              <input className="tool-search" type="search" placeholder={t('findItem')} aria-label={t('findItem')} value={q} onChange={(e) => setQ(e.target.value)} />
              <span className="muted">{t('cpOpenHint')}</span>
            </div>
            {d.rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <Rows d={d} rows={sorted(d.rows.filter((r) => !q.trim() || r.itemName.toLowerCase().includes(q.trim().toLowerCase())))} head={head} onOpen={(r) => { setItem(r.itemId); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            )}
          </div>
        )}
      </QueryView>
    </div>
  );
}

function Kpis({ d }: { d: CostDash }) {
  const { t, intlLocale } = useLocale();
  const k = d.kpis;
  const vendor = d.vendor?.name ?? '—';
  const salesPct = k.salesPrior > 0 ? Math.round(((k.sales - k.salesPrior) / k.salesPrior) * 1000) / 10 : null;
  const gmDelta = k.marginPct !== null && k.marginPctPrior !== null ? Math.round((k.marginPct - k.marginPctPrior) * 10) / 10 : null;
  const signed = (v: number | null) => (v === null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
  return (
    <div className="kpi-row" data-testid="cp-kpis">
      <div className="kpi">
        <span className="kpi-label">{t('revenue')}</span>
        <span className="kpi-value">{formatMoney(k.sales, intlLocale)}</span>
        <span className="kpi-delta">{t('vsPrior')} <Trend pct={salesPct} /></span>
      </div>
      <div className="kpi" title={k.costedPct !== null && k.costedPct < 100 ? `${t('costKnownFor')} ${k.costedPct}%` : undefined}>
        <span className="kpi-label">{t('gmCol')}</span>
        <span className="kpi-value">{k.marginPct === null ? '—' : `${k.marginPct}%`}</span>
        <span className="kpi-delta">{t('vsPrior')} <Trend pct={gmDelta} unit={` ${t('pointsUnit')}`} /></span>
      </div>
      <div className="kpi" data-testid="cp-kpi-vendor">
        <span className="kpi-label">{t('cpVendorMove').replace('{vendor}', vendor)}</span>
        <span className="kpi-value">{signed(k.vendorChangePct)}</span>
        <span className="kpi-delta">{t('cpNProducts').replace('{n}', String(k.vendorItems))}</span>
      </div>
      <div className="kpi" data-testid="cp-kpi-usda">
        <span className="kpi-label">{t('cpVsUsda').replace('{vendor}', vendor)}</span>
        <span className="kpi-value">{signed(k.vsUsdaMedianPct)}</span>
        <span className="kpi-delta">{k.usdaItems ? t('cpNProducts').replace('{n}', String(k.usdaItems)) : t('cpNoUsda')}</span>
      </div>
      <div className="kpi">
        <span className="kpi-label">{t('cpUnder')}</span>
        <span className={k.underCost + k.underFloor > 0 ? 'kpi-value bad' : 'kpi-value'}>{k.underCost} · {k.underFloor}</span>
        <span className="kpi-delta">{t('cpUnderSub')}</span>
      </div>
      <div className="kpi">
        <span className="kpi-label">{t('cpModes')}</span>
        <span className="kpi-value">{k.auto} · {k.hand}</span>
        <span className="kpi-delta">
          <Link to={`/reports/price-changes?from=${d.from}&to=${d.to}`}>{t('cpChanges').replace('{n}', String(k.priceChanges))}</Link>
        </span>
      </div>
    </div>
  );
}

/** One product's weeks: the vendor's price (columns) against the USDA middle (line), our price in the table. */
function ItemWeeks({ itemId, d, onClose }: { itemId: string; d: CostDash; onClose: () => void }) {
  const { t, intlLocale } = useLocale();
  const [tier, setTier] = useState(d.tiers[0]?.code ?? '');
  const vendor = d.vendor?.id ?? '';
  const q = useQuery({ queryKey: qk.costDashItem(itemId, vendor, tier), queryFn: ({ signal }) => api.costDashItem(itemId, { ...(vendor ? { vendor } : {}), ...(tier ? { tier } : {}) }, signal) });
  const money = (v: number | null) => (v === null ? '—' : formatMoney(v, intlLocale));
  return (
    <section data-testid="cp-item">
      <div className="chart-head">
        <h2>{q.data ? t('cpItemTitle').replace('{item}', q.data.itemName).replace('{vendor}', q.data.vendorName ?? '—') : t('loading')}</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('close')}</button>
      </div>
      <QueryView query={q}>
        {(w) => (
          <>
            <TrendChart
              label={t('cpItemTitle').replace('{item}', w.itemName).replace('{vendor}', w.vendorName ?? '—')}
              points={w.weeks.map((x) => ({ key: x.week, axis: formatDate(x.week, intlLocale, 'monthDay'), title: t('weekOf').replace('{date}', formatDate(x.week, intlLocale, 'date')) }))}
              bar={{ name: w.vendorName ?? t('cpVendor'), values: w.weeks.map((x) => x.vendor), color: 'var(--viz-1)' }}
              line={{ name: t('cpUsdaMid'), values: w.weeks.map((x) => x.usda), color: 'var(--viz-2)' }}
              format={(v) => formatMoney(v, intlLocale)}
              axisFormat={(v) => compact(v, intlLocale)}
              height={200}
            />
            <div className="toolbar">
              <label className="tool">
                <span className="label">{t('cpOurPrice')}</span>
                <select value={tier} onChange={(e) => setTier(e.target.value)}>
                  {d.tiers.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                </select>
              </label>
            </div>
            <div className="table-wrap">
              <table className="data-table" aria-label={t('cpItemTable')}>
                <thead>
                  <tr><th>{t('cpWeek')}</th><th className="num">{w.vendorName ?? t('cpVendor')}</th><th className="num">{t('cpUsdaMid')}</th><th className="num">{t('cpOurPrice')}</th></tr>
                </thead>
                <tbody>
                  {w.weeks.slice(-6).reverse().map((x) => (
                    <tr key={x.week}><td>{formatDate(x.week, intlLocale, 'date')}</td><td className="num">{money(x.vendor)}</td><td className="num">{money(x.usda)}</td><td className="num">{money(x.ours)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </QueryView>
    </section>
  );
}

function Rows({ d, rows, head, onOpen }: { d: CostDash; rows: CostDashRow[]; head: (k: SortKey, label: string) => React.ReactNode; onOpen: (r: CostDashRow) => void }) {
  const { t, intlLocale } = useLocale();
  const money = (v: number | null) => (v === null ? '—' : formatMoney(v, intlLocale));
  const signed = (v: number | null) => (v === null ? '' : `${v > 0 ? '+' : ''}${v}%`);
  const vendor = d.vendor?.name ?? t('cpVendor');
  const cols: SheetColumn<CostDashRow>[] = [
    { key: 'name', header: head('name', t('itemCol')), label: t('itemCol'), width: '200px', render: (r) => <Link to={`/items/${r.itemId}/history`}>{r.itemName}</Link> },
    { key: 'packs', header: head('packs', t('cpSold')), label: t('cpSold'), width: '64px', align: 'right', render: (r) => (r.packs ? formatQty(r.packs) : '') },
    { key: 'sales', header: head('sales', t('revenue')), label: t('revenue'), width: '96px', align: 'right', render: (r) => (r.sales ? money(r.sales) : '') },
    { key: 'gm', header: head('gm', t('gmCol')), label: t('gmCol'), width: '64px', align: 'right', render: (r) => (r.marginPct === null ? '' : `${r.marginPct}%`) },
    {
      key: 'cost', header: head('cost', t('costCol')), label: t('costCol'), width: '120px', align: 'right',
      render: (r) => (r.cost === null ? '—' : <>{money(r.cost)}{r.costVendor ? <span className="cell-sub">{r.costVendor}{r.costSource ? ` · ${r.costSource}` : ''}</span> : null}</>),
    },
    {
      key: 'vendor', header: head('vendor', vendor), label: vendor, width: '104px', align: 'right',
      render: (r) => (r.vendorNow === null ? '—' : <>{money(r.vendorNow)}{r.vendorThen !== null && r.vendorThen !== r.vendorNow ? <span className="cell-sub">{t('cpWas').replace('{price}', money(r.vendorThen))}</span> : null}</>),
    },
    { key: 'change', header: head('change', t('cpChange')), label: t('cpChange'), width: '72px', align: 'right', render: (r) => signed(r.vendorChangePct), cellClass: (r) => ((r.vendorChangePct ?? 0) > 0 ? 'warn' : undefined) },
    {
      key: 'other', header: head('other', t('cpBestOther')), label: t('cpBestOther'), width: '120px', align: 'right',
      render: (r) => (r.bestOther ? <>{money(r.bestOther.price)}<span className="cell-sub">{r.bestOther.vendorName}</span></> : ''),
      cellClass: (r) => (r.bestOther && r.vendorNow !== null && r.bestOther.price < r.vendorNow ? 'good' : undefined),
    },
    {
      key: 'usda', header: head('usda', t('cpUsdaMid')), label: t('cpUsdaMid'), width: '104px', align: 'right',
      render: (r) => (r.usda ? <span title={`${r.usda.market} · ${formatDate(r.usda.date, intlLocale, 'date')}`}>{money(r.usda.mid)}<span className="cell-sub">{money(r.usda.low)}–{money(r.usda.high)}</span></span> : ''),
    },
    { key: 'vs', header: head('vs', t('cpVsUsdaCol')), label: t('cpVsUsdaCol'), width: '72px', align: 'right', render: (r) => signed(r.vsUsdaPct), cellClass: (r) => ((r.vsUsdaPct ?? 0) > 10 ? 'warn' : undefined) },
    ...d.tiers.map((tier, ti): SheetColumn<CostDashRow> => ({
      key: `tier:${tier.code}`, header: tier.name, label: tier.name, width: '104px', align: 'right',
      render: (r) => {
        const p = r.prices[ti];
        if (!p || p.price === null) return '—';
        return <>{money(p.price)}<span className="cell-sub">{p.gmPct === null ? '' : `${p.gmPct}%`}{p.mode ? ` · ${t(p.mode === 'auto' ? 'priceAuto' : 'priceHand')}` : ''}</span></>;
      },
      cellClass: (r) => {
        const p = r.prices[ti];
        return p && p.price !== null && r.cost !== null && p.price < r.cost ? 'bad' : undefined;
      },
    })),
  ];
  return <Sheet label={t('costsPricesTitle')} columns={cols} rows={rows} rowKey={(r) => r.itemId} rowLabel={(r) => r.itemName} stickyFirst minWidth={`${1120 + d.tiers.length * 104}px`} onOpen={onOpen} />;
}
